import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Button, Tag } from 'antd'
import { CheckOutlined, WarningOutlined } from '@ant-design/icons'

import { formatTimeRange } from './journeyTime'
import { cancelKey, paymentKey, reasonKey, wordsFor } from './journeyWords'
import { CHECKOUT_FAILED, SELECT_PAYMENT_METHOD, productsOf } from './journeyFacts'
import { DEPOSIT_PAID, ORDER_CANCELLED, isOrderStep } from './afterOrderFacts'
import { isMessage } from './journeyDays'
import { ChatMessages, ChatToggle } from './LineChat'
import {
  Clock,
  Muted,
  ProductLine,
  ProductName,
  StepBody,
  StepGrid,
  StepLabel,
  StepLine
} from './journeyStyles'

const PRODUCTS_SHOWN = 3

const detailWords = (journey, step, detail, lang) => {
  if (step.name === CHECKOUT_FAILED) return wordsFor(journey, reasonKey(detail), lang)
  if (step.name === SELECT_PAYMENT_METHOD) return wordsFor(journey, paymentKey(detail), lang)
  if (step.name === ORDER_CANCELLED) return wordsFor(journey, cancelKey(detail), lang)
  return detail
}

const StepRow = ({ step, journey, lang, orderIds, credited }) => {
  const { t } = useTranslation('inquiries')
  const [showAllProducts, setShowAllProducts] = useState(false)
  const [chatOpen, setChatOpen] = useState(false)
  const chat = step.chat || []
  const fromUs = isMessage(step)
  const ofThisOrder = isOrderStep(step)
  const isFailed = step.name === CHECKOUT_FAILED
  const products = productsOf(step, journey.items, orderIds)
  const shownProducts = showAllProducts ? products : products.slice(0, PRODUCTS_SHOWN)
  const details = (step.details || []).map((detail) => detailWords(journey, step, detail, lang))
  const counts = [
    step.count > 1 && t('orderJourney.times', { n: step.count }),
    products.length > 0 && t('orderJourney.products', { count: products.length })
  ].filter(Boolean)

  return (
    <StepGrid $tinted={fromUs}>
      <Clock>{formatTimeRange(step.first_at, step.last_at)}</Clock>
      <StepBody>
        <StepLine>
          {fromUs && <Tag color='blue' style={{ marginInlineEnd: 0 }}>{t('orderJourney.weSent')}</Tag>}
          {ofThisOrder && <Tag style={{ marginInlineEnd: 0 }}>{t('orderJourney.thisOrderTag')}</Tag>}
          {isFailed && <WarningOutlined style={{ color: 'var(--journey-warning)' }} />}
          <StepLabel $strong={fromUs || isFailed || step.name === DEPOSIT_PAID}>
            {wordsFor(journey, step.name, lang)}
          </StepLabel>
          {counts.length > 0 && <Muted>· {counts.join(' · ')}</Muted>}
          {details.length > 0 && <span>— {details.join(', ')}</span>}
          {credited && <Tag color='green' style={{ marginInlineEnd: 0 }}>{t('orderJourney.creditedTag')}</Tag>}
          {chat.length > 0 && <ChatToggle open={chatOpen} onToggle={() => setChatOpen(!chatOpen)} />}
        </StepLine>
        {chat.length > 0 && <Muted $small>{t('orderJourney.lineHint')}</Muted>}
        {chatOpen && <ChatMessages chat={chat} count={step.count} journey={journey} lang={lang} />}
        {products.length > 0 && (
          <ProductLine>
            {shownProducts.map((product) => (
              <ProductName key={product.id} $inOrder={product.inOrder}>
                {product.inOrder && (
                  <CheckOutlined aria-label={t('orderJourney.inOrder')} style={{ color: 'var(--journey-success)' }} />
                )}
                {product.name || `#${product.id}`}
              </ProductName>
            ))}
            {products.length > PRODUCTS_SHOWN && (
              <Button type='link' size='small' style={{ padding: 0 }} onClick={() => setShowAllProducts(!showAllProducts)}>
                {showAllProducts
                  ? t('orderJourney.less')
                  : t('orderJourney.more', { n: products.length - PRODUCTS_SHOWN })}
              </Button>
            )}
          </ProductLine>
        )}
      </StepBody>
    </StepGrid>
  )
}

StepRow.propTypes = {
  step: PropTypes.object.isRequired,
  journey: PropTypes.object.isRequired,
  lang: PropTypes.string.isRequired,
  orderIds: PropTypes.instanceOf(Set).isRequired,
  credited: PropTypes.bool
}

export { StepRow }
